import React, { useState } from 'react';
import { useApp } from '../../AppContext';
import { ALL_HOURS, WeeklyDefaultItem } from '../../types';

export default function WeeklyEditor() {
  const { weeklyDefaults, updateWeeklyDefault } = useApp();

  return (
    <div className="bg-forest p-6 rounded-3xl border border-sage/15 space-y-4">
      <div>
        <h3 className="font-serif text-xl">Settimana tipo</h3>
        <p className="text-xs text-sage mt-1">Apri/chiudi i giorni e definisci le ore disponibili. Vale per tutte le settimane salvo eccezioni.</p>
      </div>

      <div className="space-y-3">
        {weeklyDefaults.map(day => (
          <WeeklyRow key={day.id} day={day} onSave={updateWeeklyDefault} />
        ))}
      </div>
    </div>
  );
}

function WeeklyRow({
  day,
  onSave,
}: {
  day: WeeklyDefaultItem;
  onSave: (id: number, patch: Partial<Pick<WeeklyDefaultItem, 'is_open' | 'hours'>>) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleOpen = async () => {
    setSaving(true);
    try { await onSave(day.id, { is_open: !day.is_open }); }
    finally { setSaving(false); }
  };

  const toggleHour = async (h: string) => {
    setSaving(true);
    const next = day.hours.includes(h) ? day.hours.filter(x => x !== h) : [...day.hours, h].sort();
    try { await onSave(day.id, { hours: next }); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-forestDark/40 rounded-2xl border border-sage/15 overflow-hidden">
      <div className="flex items-center justify-between p-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={toggleOpen}
            disabled={saving}
            className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${day.is_open ? 'bg-emerald-600' : 'bg-stone-500/40'} disabled:opacity-60`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-cream transition-transform ${day.is_open ? 'translate-x-5' : ''}`} />
          </button>
          <div className="min-w-0">
            <div className="font-bold text-sm text-cream">{day.weekday_name}</div>
            <div className="text-[11px] text-sage truncate">
              {day.is_open
                ? (day.hours.length === 0 ? 'Aperto ma senza ore' : `${day.hours.length} fasce: ${day.hours.join(', ')}`)
                : 'Chiuso'}
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-sage hover:text-cream smooth-transition shrink-0"
        >
          {expanded ? '▲ chiudi' : '▼ ore'}
        </button>
      </div>

      {expanded && (
        <div className="p-4 pt-0 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
          {ALL_HOURS.map(h => {
            const active = day.hours.includes(h);
            return (
              <button
                key={h}
                onClick={() => toggleHour(h)}
                disabled={!day.is_open || saving}
                className={`p-1.5 rounded text-[11px] font-semibold border transition-all ${
                  active ? 'bg-forest border-sage text-cream'
                         : 'bg-forestDark/55 border-sage/20 text-sage/70'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {h}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
